/*
 * Наполнение шкафа по замечанию Сергея № 26: «полка, ящик деревянный,
 * обувница деревянная (плоская полка с бортиком 30–50 мм по лицу), корзина
 * сетчатая, корзина „под кожу“, брючница — кратна 600, 700, 800, 900».
 *
 * Обувница — обычная полка из декора корпуса с бортиком спереди; шумная
 * металлическая этажерка ушла. Брючница ставится только в пенал шириной 600,
 * 700, 800 или 900 мм — под эти ширины выпускают механизм.
 */
import { CARCASS_MM, HALF_CARCASS_MM } from "./carcass";
import type { ItemType } from "../types";

/** Бортик обувницы над полкой, мм (Сергей: 30–50; 40 — до его ответа). */
export const SHOE_SHELF_LIP_MM = 40;
/** Половина толщины полки: отметка полки — её середина. */
export const SHELF_HALF_MM = HALF_CARCASS_MM;

/** Брюки висят под рамкой брючницы на эту высоту. */
export const TROUSER_RACK_DROP_MM = 450;
/** Ширины пенала, под которые выпускают брючницы и корзины. */
export const TROUSER_RACK_WIDTHS_MM = [600, 700, 800, 900] as const;
/**
 * Корзины (сетчатая и «под кожу») и брючница выпускаются только под пенал 600,
 * 700, 800 или 900 мм; в пенал 1200 без стойки — только ящики, полки, штанги
 * (видео Сергея 31.10).
 */
export const NOMINAL_WIDTH_FITTINGS: ReadonlySet<ItemType> = new Set<ItemType>(["basket", "leatherBasket", "trouserRack"]);
/** Две боковины пенала. */
const SIDES_MM = CARCASS_MM * 2;
/** Допуск ширины в свету: стойка двух соседних пеналов общая, пенал на полстойки–стойку уже номинала. */
const TROUSER_RACK_TOLERANCE_MM = 30;

/** Номинал пенала по ширине отсека в свету или null, если брючница не встаёт. */
export const trouserRackNominalWidth = (clearWidthMm: number) => {
  const outer = clearWidthMm + SIDES_MM;
  const nominal = TROUSER_RACK_WIDTHS_MM.find((width) => Math.abs(outer - width) <= TROUSER_RACK_TOLERANCE_MM);
  return nominal ?? null;
};

/** Встаёт ли деталь в отсек этой ширины. У корзин и брючницы — только номиналы 600–900. */
export const fittingFitsWidth = (type: ItemType, clearWidthMm: number, minimumWidthMm: number) =>
  NOMINAL_WIDTH_FITTINGS.has(type)
    ? trouserRackNominalWidth(clearWidthMm) !== null
    : clearWidthMm >= minimumWidthMm;

/** Габарит по высоте относительно отметки детали, мм. */
export const fittingVerticalOffsets = (type: ItemType, height?: number): [number, number] | null => {
  switch (type) {
    case "shoeRack":
      return [-SHELF_HALF_MM, SHELF_HALF_MM + SHOE_SHELF_LIP_MM];
    case "leatherBasket":
      return [-(height ?? 160), 0];
    case "trouserRack":
      return [-(height ?? TROUSER_RACK_DROP_MM), 0];
    default:
      return null;
  }
};

export const TROUSER_RACK_HINT = `Брючница ставится в пенал шириной ${TROUSER_RACK_WIDTHS_MM.join(", ")} мм`;

const NOMINAL_FITTING_NAMES: Partial<Record<ItemType, string>> = {
  basket: "Сетчатая корзина",
  leatherBasket: "Корзина «под кожу»",
  trouserRack: "Брючница"
};

/** Подсказка: деталь выпускают только под пенал 600–900 мм. */
export const nominalFittingHint = (type: ItemType) =>
  `${NOMINAL_FITTING_NAMES[type] ?? "Деталь"} ставится в пенал шириной ${TROUSER_RACK_WIDTHS_MM.join(", ")} мм`;
